/**
 * 展示预约列表 Composable
 * 提供 activeAppointments、displayedAppointments、patientSlotPool（固定 8 槽元素池）
 */

import config from '@/config';
import logger from '@/utils/logger';

/** 患者卡片 DOM / 图表槽位池大小（轮播只换数据，不销毁 DOM） */
export const PATIENT_SLOT_POOL_SIZE = Number(config.performance?.patientSlotPoolSize) || 8;

export default function useDisplayedAppointments() {
  return {
    methods: {
      /**
       * 当前每页可见槽位数：半屏欢迎语 2，全屏 4
       */
      getDisplayPageSize() {
        if (this.isVideoPage || this.isFullscreenWelcomePage) {
          return 0;
        }
        const isHalfScreenWelcome =
          this.welcomeMessage &&
          !(
            this.welcomeConfig &&
            (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1')
          );
        return isHalfScreenWelcome ? 2 : 4;
      },

      /**
       * 筛选并排序后的治疗中 / 已结束患者列表（供分页与槽位绑定共用）
       */
      getDisplayPatientLists() {
        const allAppointments = (this.appointmentData || []).filter((item) => {
          if (!item || !item.startTime || !item.endTime) {
            return false;
          }
          return true;
        });

        const ongoingPatients = allAppointments
          .filter((item) => this.getTreatmentStatus(item) === '治疗中')
          .sort((a, b) => {
            const va = !!(a.isMock || a.dataType === 1 || !a.roomId);
            const vb = !!(b.isMock || b.dataType === 1 || !b.roomId);
            if (va !== vb) return va ? -1 : 1;
            const remainingTimeA = this.getRemainingTimeInMinutes(a);
            const remainingTimeB = this.getRemainingTimeInMinutes(b);
            return remainingTimeA - remainingTimeB;
          });

        const completedPatients = allAppointments
          .filter((item) => this.getTreatmentStatus(item) === '已结束')
          .sort((a, b) => {
            const now = new Date();
            const getEndDateTime = (appt) => {
              let appointmentDate = appt.appointmentDate
                ? new Date(appt.appointmentDate)
                : new Date(appt.startTime);
              if (isNaN(appointmentDate.getTime())) {
                appointmentDate = new Date();
              }
              const appointmentDateOnly = new Date(appointmentDate);
              appointmentDateOnly.setHours(0, 0, 0, 0);
              const withDate = this.parseTimeStringWithDate(appt.endTime, appointmentDateOnly);
              const fallback = this.parseTimeString(appt.endTime);
              return withDate || fallback || new Date(0);
            };
            const endA = getEndDateTime(a);
            const endB = getEndDateTime(b);
            const diffA = Math.abs(now.getTime() - endA.getTime());
            const diffB = Math.abs(now.getTime() - endB.getTime());
            return diffA - diffB;
          });

        return { ongoingPatients, completedPatients };
      },

      /**
       * 指定页码的患者列表（含已结束补位），与 displayedAppointments 逻辑一致
       * @param {number} pageIndex
       */
      getPatientsForPage(pageIndex) {
        const pageSize = this.getDisplayPageSize();
        if (pageSize === 0) {
          return [];
        }

        const { ongoingPatients, completedPatients } = this.getDisplayPatientLists();

        if (ongoingPatients.length === 0 && completedPatients.length === 0) {
          const emptyResult = [];
          for (let i = 0; i < pageSize; i++) {
            emptyResult.push(null);
          }
          return emptyResult;
        }

        const startIndex = pageIndex * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageOngoing = ongoingPatients.slice(startIndex, endIndex);

        const needToFill = pageSize - currentPageOngoing.length;

        let usedCompletedCount = 0;
        for (let page = 0; page < pageIndex; page++) {
          const pageStart = page * pageSize;
          const pageOngoing = ongoingPatients.slice(pageStart, pageStart + pageSize);
          const pageOngoingCount = pageOngoing.length;
          const pageNeedFill = Math.max(0, pageSize - pageOngoingCount);
          usedCompletedCount += pageNeedFill;
        }

        const availableCompleted = completedPatients.slice(usedCompletedCount);
        const fillPatients = availableCompleted.slice(0, needToFill);
        const result = [...currentPageOngoing, ...fillPatients];

        while (result.length < pageSize) {
          result.push(null);
        }

        return result;
      },

      /** 空槽位占位（视频页 / 全屏欢迎语） */
      buildEmptyPatientSlotPool() {
        const slots = [];
        for (let i = 0; i < PATIENT_SLOT_POOL_SIZE; i++) {
          slots.push({ slotIndex: i, item: null, visible: false });
        }
        return slots;
      },
    },

    computed: {
      activeAppointments() {
        const filteredAppointments = this.appointmentData.filter((item) => {
          if (!item || !item.startTime || !item.endTime) {
            return false;
          }

          const now = new Date();
          const startTime = this.parseTimeString(item.startTime);
          const endTime = this.parseTimeString(item.endTime);

          if (!startTime || !endTime) {
            return false;
          }

          return true;
        });

        let sortedAppointments = filteredAppointments.sort((a, b) => {
          const statusA = this.getTreatmentStatus(a);
          const statusB = this.getTreatmentStatus(b);

          const getPriority = (status) => {
            if (status === '治疗中') return 1;
            if (status === '治疗未开始') return 2;
            if (status === '已结束') return 3;
            return 4;
          };

          const priorityA = getPriority(statusA);
          const priorityB = getPriority(statusB);

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          if (statusA === '治疗中' && statusB === '治疗中') {
            const va = !!(a.isMock || a.dataType === 1 || !a.roomId);
            const vb = !!(b.isMock || b.dataType === 1 || !b.roomId);
            if (va !== vb) return va ? -1 : 1;
            const remainingTimeA = this.getRemainingTimeInMinutes(a);
            const remainingTimeB = this.getRemainingTimeInMinutes(b);
            return remainingTimeA - remainingTimeB;
          } else if (statusA === '已结束' && statusB === '已结束') {
            const now = new Date();
            const getEndDateTime = (appt) => {
              let appointmentDate = appt.appointmentDate ? new Date(appt.appointmentDate) : new Date(appt.startTime);
              if (isNaN(appointmentDate.getTime())) {
                appointmentDate = new Date();
              }
              const appointmentDateOnly = new Date(appointmentDate);
              appointmentDateOnly.setHours(0, 0, 0, 0);
              const withDate = this.parseTimeStringWithDate(appt.endTime, appointmentDateOnly);
              const fallback = this.parseTimeString(appt.endTime);
              return withDate || fallback || new Date(0);
            };
            const endA = getEndDateTime(a);
            const endB = getEndDateTime(b);
            const diffA = Math.abs(now.getTime() - endA.getTime());
            const diffB = Math.abs(now.getTime() - endB.getTime());
            return diffA - diffB;
          } else if (statusA === '治疗未开始' && statusB === '治疗未开始') {
            const startTimeA = this.parseTimeString(a.startTime);
            const startTimeB = this.parseTimeString(b.startTime);
            return startTimeA - startTimeB;
          }

          return 0;
        });

        const MAX_VIRTUAL_APPOINTMENTS = 4;
        const virtualAppointments = sortedAppointments.filter(
          (item) => item && (item.isMock || item.dataType === 1 || !item.roomId)
        );

        if (virtualAppointments.length > MAX_VIRTUAL_APPOINTMENTS) {
          const byPriority = [...virtualAppointments].sort((a, b) => {
            const pa = this.getTreatmentStatus(a) === '治疗中' ? 0 : 1;
            const pb = this.getTreatmentStatus(b) === '治疗中' ? 0 : 1;
            if (pa !== pb) return pa - pb;
            return 0;
          });
          const keepSet = new Set(byPriority.slice(0, MAX_VIRTUAL_APPOINTMENTS));
          logger.debug(
            `[虚拟数据条数控制] activeAppointments 中检测到 ${virtualAppointments.length} 个虚拟数据，保留 ${MAX_VIRTUAL_APPOINTMENTS} 条`,
            {
              totalVirtual: virtualAppointments.length,
              kept: [...keepSet].map((i) => i.patientName),
            }
          );
          sortedAppointments = sortedAppointments.filter((item) => {
            if (!item) return false;
            const isVirtual = item.isMock || item.dataType === 1 || !item.roomId;
            if (isVirtual) {
              return keepSet.has(item);
            }
            return true;
          });
        }

        const ongoingPatients = sortedAppointments.filter(
          (item) => this.getTreatmentStatus(item) === '治疗中'
        );
        const completedPatients = sortedAppointments.filter(
          (item) => this.getTreatmentStatus(item) === '已结束'
        );
        const upcomingPatients = sortedAppointments.filter(
          (item) => this.getTreatmentStatus(item) === '治疗未开始'
        );

        let finalAppointments = [...ongoingPatients];

        const pageSize = this.welcomeMessage ? 2 : 4;
        const ongoingCount = finalAppointments.length;
        if (this.welcomeMessage) {
          const targetCount = Math.max(pageSize, Math.ceil(ongoingCount / pageSize) * pageSize);
          let needToAdd = Math.max(0, targetCount - finalAppointments.length);
          if (needToAdd > 0 && completedPatients.length > 0) {
            const patientsToAdd = completedPatients.slice(0, needToAdd);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          let stillNeed = targetCount - finalAppointments.length;
          if (stillNeed > 0 && upcomingPatients.length > 0) {
            const patientsToAdd = upcomingPatients.slice(0, stillNeed);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          if (ongoingPatients.length === 0 && completedPatients.length > 0) {
            finalAppointments = completedPatients.slice(0, pageSize);
          }
          if (ongoingPatients.length === 0 && completedPatients.length === 0 && upcomingPatients.length > 0) {
            const targetCount = Math.ceil(upcomingPatients.length / pageSize) * pageSize;
            finalAppointments = upcomingPatients.slice(0, targetCount);
          }
        } else {
          const currentCount = finalAppointments.length;
          const nextMultiple = Math.max(pageSize, Math.ceil(currentCount / pageSize) * pageSize);
          let needToAdd = nextMultiple - currentCount;
          if (needToAdd > 0 && completedPatients.length > 0) {
            const patientsToAdd = completedPatients.slice(0, needToAdd);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          let stillNeed = nextMultiple - finalAppointments.length;
          if (stillNeed > 0 && upcomingPatients.length > 0) {
            const patientsToAdd = upcomingPatients.slice(0, stillNeed);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          if (ongoingPatients.length === 0 && completedPatients.length > 0) {
            finalAppointments = completedPatients.slice(0, pageSize);
          }
          if (ongoingPatients.length === 0 && completedPatients.length === 0 && upcomingPatients.length > 0) {
            const targetCount = Math.ceil(upcomingPatients.length / pageSize) * pageSize;
            finalAppointments = upcomingPatients.slice(0, targetCount);
          }
        }

        return finalAppointments;
      },

      displayedAppointments() {
        if (this.isVideoPage || this.isFullscreenWelcomePage) {
          return [];
        }
        return this.getPatientsForPage(this.currentPage);
      },

      /**
       * 固定 8 槽元素池：DOM 始终挂载，轮播仅更新槽位绑定数据
       * visible=true 的槽位对应当前页；其余槽位隐藏但保留 DOM / 图表实例
       */
      patientSlotPool() {
        if (this.isVideoPage || this.isFullscreenWelcomePage) {
          return this.buildEmptyPatientSlotPool();
        }

        const pageSize = this.getDisplayPageSize();
        const pagePatients = this.getPatientsForPage(this.currentPage);
        const slots = [];

        for (let i = 0; i < PATIENT_SLOT_POOL_SIZE; i++) {
          const visible = i < pageSize;
          slots.push({
            slotIndex: i,
            item: visible ? pagePatients[i] || null : null,
            visible,
          });
        }

        return slots;
      },
    },
  };
}
